/**
 * Size store adapter (Stage 2 of docs/V1_TO_V2_MIGRATION.md).
 *
 * Stores the sizes array in the customer metafield filatori_v2.sizes. Each size is
 * identified by its `timestamp`. Knows only that shape, nothing about the UI.
 * Shape: "Data shape" in docs/V1_TO_V2_MIGRATION.md.
 *
 * - Reads: Liquid-rendered value first (#FilatoriSizesData), CA API as a fallback.
 * - Writes: always re-read through the CA API, apply one change, then `metafieldsSet`
 *   with the read's `compareDigest`. On STALE_OBJECT the change is re-applied to a
 *   fresh read and retried once.
 * - CA API calls go through FilatoriAuth.request(), which may redirect to get a token.
 *   The promise then never resolves, so callers must persist unsaved work
 *   (e.g. localStorage.newSize) before reading from the API or writing.
 *
 * Requires window.FilatoriAuth (assets/filatori-auth.js).
 * Public API: window.FilatoriSizes
 */
(function () {
  const NAMESPACE = 'filatori_v2';
  const KEY = 'sizes';
  // JSON metafield writes are capped at 128KB
  const MAX_BYTES = 128 * 1024;

  const READ_QUERY = `
    query FilatoriReadSizes {
      customer {
        id
        sizes: metafield(namespace: "${NAMESPACE}", key: "${KEY}") {
          value
          compareDigest
        }
      }
    }
  `;

  const WRITE_MUTATION = `
    mutation FilatoriWriteSizes($metafields: [MetafieldsSetInput!]!) {
      metafieldsSet(metafields: $metafields) {
        metafields {
          compareDigest
        }
        userErrors {
          field
          message
          code
        }
      }
    }
  `;

  const liquidData = readLiquidData();

  window.FilatoriSizes = {
    getSizes,
    saveSize,
    removeSize,
    updateSizes,
  };

  // Optional: pages without the Liquid data read through the API
  function readLiquidData() {
    const el = document.querySelector('#FilatoriSizesData');
    if (!el) {
      return null;
    }

    return JSON.parse(el.textContent);
  }

  /**
   * Resolves to the sizes array ([] when there are none), or null when the customer
   * is not logged in. `options.fresh` skips the Liquid value and reads through the API.
   */
  async function getSizes(options) {
    const opts = options || {};

    if (!window.FilatoriAuth.isLoggedIn()) {
      return null;
    }

    if (!opts.fresh && liquidData) {
      return toSizes(liquidData.sizes);
    }

    const remote = await readRemote(opts.returnTo);
    if (!remote) {
      return null;
    }

    return remote.sizes;
  }

  /**
   * Adds a size, or replaces the one with the same `timestamp`. Sets `updatedAt`, and
   * `createdAt` for new sizes. Resolves to the saved sizes array.
   */
  function saveSize(size, returnTo) {
    return updateSizes(function setSize(sizes) {
      const now = new Date().toISOString();
      const existing = sizes.find(function isSame(item) {
        return item.timestamp === size.timestamp;
      });

      const saved = Object.assign({}, size, {
        createdAt: existing ? existing.createdAt : size.createdAt || createdAtFrom(size.timestamp) || now,
        updatedAt: now,
      });

      const others = sizes.filter(function isOther(item) {
        return item.timestamp !== size.timestamp;
      });

      return sortByTimestamp(others.concat([saved]));
    }, returnTo);
  }

  /** Removes the size with this timestamp. Resolves to the saved sizes array. */
  function removeSize(timestamp, returnTo) {
    return updateSizes(function deleteSize(sizes) {
      return sizes.filter(function isOther(item) {
        return item.timestamp !== timestamp;
      });
    }, returnTo);
  }

  /**
   * Reads the current sizes, passes a copy to `applyChange` and writes the array it
   * returns. `applyChange` may run twice (retry after STALE_OBJECT), so it must only
   * change the sizes it is given. Resolves to the saved sizes array.
   * Errors have a `code`: not_logged_in, invalid_shape, too_large, stale, user_errors.
   */
  async function updateSizes(applyChange, returnTo) {
    const first = await tryUpdate(applyChange, returnTo);
    if (first.ok) {
      return first.sizes;
    }

    if (first.code !== 'STALE_OBJECT') {
      throw createError('user_errors', first.userErrors);
    }

    const retry = await tryUpdate(applyChange, returnTo);
    if (retry.ok) {
      return retry.sizes;
    }

    throw createError(retry.code === 'STALE_OBJECT' ? 'stale' : 'user_errors', retry.userErrors);
  }

  async function tryUpdate(applyChange, returnTo) {
    const remote = await readRemote(returnTo);
    if (!remote) {
      throw createError('not_logged_in');
    }

    // Never overwrite a value in another shape
    if (remote.value !== null && !isValidSizes(remote.value)) {
      throw createError('invalid_shape');
    }

    const sizes = applyChange(clone(remote.sizes));
    if (!isValidSizes(sizes)) {
      throw createError('invalid_shape');
    }

    const value = JSON.stringify(sizes);
    if (new TextEncoder().encode(value).length > MAX_BYTES) {
      throw createError('too_large');
    }

    const input = {
      ownerId: remote.customerId,
      namespace: NAMESPACE,
      key: KEY,
      type: 'json',
      value,
    };
    if (remote.compareDigest) {
      input.compareDigest = remote.compareDigest;
    }

    const data = await window.FilatoriAuth.request(WRITE_MUTATION, { metafields: [input] }, returnTo);
    if (!data) {
      throw createError('not_logged_in');
    }

    const userErrors = data.metafieldsSet.userErrors;
    if (userErrors.length) {
      return { ok: false, code: userErrors[0].code, userErrors };
    }

    // Keep later getSizes() calls on this page in sync with the write
    if (liquidData) {
      liquidData.sizes = clone(sizes);
    }

    return { ok: true, sizes };
  }

  // Resolves to { customerId, value, sizes, compareDigest }, or null when not logged in
  async function readRemote(returnTo) {
    const data = await window.FilatoriAuth.request(READ_QUERY, {}, returnTo);
    if (!data || !data.customer) {
      return null;
    }

    const metafield = data.customer.sizes;
    const value = metafield ? JSON.parse(metafield.value) : null;

    return {
      customerId: data.customer.id,
      value,
      sizes: toSizes(value),
      compareDigest: metafield ? metafield.compareDigest : null,
    };
  }

  function toSizes(value) {
    if (value === null || value === undefined) {
      return [];
    }

    if (!isValidSizes(value)) {
      console.warn('FilatoriSizes: metafield value has an unexpected shape, ignoring it');
      return [];
    }

    return clone(value);
  }

  // An array of sizes, each with a string timestamp and a measurements object
  function isValidSizes(value) {
    if (!Array.isArray(value)) {
      return false;
    }

    return value.every(function isValidSize(size) {
      return isPlainObject(size) && typeof size.timestamp === 'string' && isPlainObject(size.measurements);
    });
  }

  function sortByTimestamp(sizes) {
    return sizes.slice().sort(function byTimestamp(a, b) {
      return Number(a.timestamp) - Number(b.timestamp);
    });
  }

  function createdAtFrom(timestamp) {
    const ms = Number(timestamp);
    if (!ms) {
      return null;
    }

    return new Date(ms).toISOString();
  }

  function isPlainObject(value) {
    return Boolean(value) && typeof value === 'object' && !Array.isArray(value);
  }

  function clone(value) {
    return JSON.parse(JSON.stringify(value));
  }

  function createError(code, userErrors) {
    const error = new Error(`FilatoriSizes: ${code}`);
    error.code = code;
    error.userErrors = userErrors || [];

    return error;
  }
})();
